import React, { useCallback, useState } from 'react';
import { Nullable } from 'global';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { useForm, FormProvider } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DEAL_BUILDER_DEFAULT_VALUES,
  DEAL_BUILDER_QUERY_PARAM_PROGRESS,
  DEAL_BUILDER_QUERY_PARAM_STEP,
} from '../constants';
import { dealBuilderFormSchema } from '../model/schema';
import { DealBuilderCreditApplicationSource, DealBuilderEquipmentSource, DealBuilderFormValues } from '../types';
import { DealBuilderWrapper } from './DealBuilderWrapper';

interface Props {
  vo: Nullable<VendorOpportunity>;
  creditApplication: Nullable<DealBuilderCreditApplicationSource>;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
}

export const DealBuilderTab = ({ vo, creditApplication, equipmentData }: Props) => {
  const [remountKey, setRemountKey] = useState(0);
  const [activeProposalId, setActiveProposalId] = useState<Nullable<string>>(null);
  const { replace } = useHistory();
  const location = useLocation();

  const formMethods = useForm<DealBuilderFormValues>({
    resolver: zodResolver(dealBuilderFormSchema),
    defaultValues: DEAL_BUILDER_DEFAULT_VALUES,
  });

  const remount = useCallback(
    (proposalId?: string) => {
      if (proposalId !== undefined) setActiveProposalId(proposalId);
      formMethods.reset(DEAL_BUILDER_DEFAULT_VALUES);
      const params = new URLSearchParams(location.search);
      params.delete(DEAL_BUILDER_QUERY_PARAM_STEP);
      params.delete(DEAL_BUILDER_QUERY_PARAM_PROGRESS);
      replace({ pathname: location.pathname, hash: location.hash, search: params.toString() });
      setRemountKey(k => k + 1);
    },
    [formMethods, location, replace]
  );

  return (
    <FormProvider {...formMethods} key={remountKey}>
      <DealBuilderWrapper
        vo={vo}
        creditApplication={creditApplication}
        equipmentData={equipmentData}
        activeProposalId={activeProposalId}
        onRemount={remount}
      />
    </FormProvider>
  );
};
